import React, { useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { AppContext } from "../context/app.context";
import { ModelCxContext } from "../context/modelCx.context";
import {
  MONGODB_AWSGW_QUERIES,
  MONGODB_AWSGW_APIKEY,
} from "../utility/keys";
import { resolveBackendMode } from "../utility/backendConfig";
import { refs } from "../convex/apiRefs";
import { convexReadEventLog } from "../convex/convexRead";
import { buildEventLogByDocumentIdRequestBody } from "../utility/buildEventLogByDocumentIdRequestBody";
import { parseEventLogResponse } from "../utility/parseEventLogResponse";
import { buildBlackoutDatesAuditByKellerIdRequestBody } from "../utility/buildBlackoutDatesAuditByKellerIdRequestBody";
import { parseBlackoutDatesAuditResponse } from "../utility/parseBlackoutDatesAuditResponse";
import { fetchAllLambdaChunks } from "../utility/fetchAllLambdaChunks";
import EventHistoryList from "./EventHistoryList";
import GridSpinner from "./GridSpinner";

/**
 * Time Machine #114 Phase 5 + 6 — History feed for the JobsDetailModal when
 * in time-travel mode.
 *
 * Phase 5: fetches every EventLog entry for one ActiveQueue document via the
 * Lambda's `getEventLogByDocumentId` method.
 *
 * Phase 6: ALSO fetches every BlackoutDatesAudit invocation for the same
 * customer (matched by `_kf_KellerID` === `inputs.kellerId`) via the
 * Lambda's `getBlackoutDatesAuditByKellerId` method, and merges both streams
 * into a single chronological list. Audit rows render with a 🌐 prefix and
 * "Customer Web Portal queried availability" label; double-click scrubs the
 * time-travel slider to the customer's portal-query moment so the support
 * team can see WHAT availability the customer was offered.
 *
 * Props:
 *   documentId — ObjectId hex string for the ActiveQueue doc (Phase 5 leg)
 *   kellerId   — "ID-XXXXXXX" string from the job's `_kf_KellerID`
 *                (Phase 6 leg). When null/missing, the audit query is
 *                skipped and the History feed shows EventLog entries only.
 *   onScrubbed — optional fn() called when user double-clicks a row to
 *                scrub the slider; typically used by the parent modal to
 *                close itself after scrub
 *
 * The two queries fire independently. The merged-and-sorted result is
 * passed to <EventHistoryList>; both spinner and error states surface for
 * either leg without blocking the other.
 */
const JobHistorySection = ({ documentId, kellerId, onScrubbed, readOnly = false, database }) => {
  const { appEnvirons } = useContext(AppContext) || {};
  // #292 item 5 — an embedding host (Ticket Master) that is pinned to PROD can
  // pass `database` to force the Lambda read env, overriding the signed-in
  // env's database. Default preserves the original env-derived behavior.
  const mongoDatabase = database || appEnvirons?.mongoDb || "Warehouses";
  // #191 Stage 3 — convexOnly reads Time Machine history from Convex, never the
  // Lambda. (TM always reads PROD inside Convex — source hardcoded, #121.)
  const { tsUserDocument } = useContext(ModelCxContext) || {};
  const isConvexOnly = resolveBackendMode(tsUserDocument) === "convexOnly";

  const isValidDocId =
    typeof documentId === "string" && /^[0-9a-fA-F]{24}$/.test(documentId);
  const isValidKellerId =
    typeof kellerId === "string" && /^ID-\d{1,15}$/.test(kellerId);

  // Diagnostic: surface what the parent passed when the validation fails.
  // Helps debug "No record selected" when the parent thinks it's passing _id.
  if (!isValidDocId && documentId !== null && documentId !== undefined) {
    console.warn(
      "%cJobHistorySection: documentId failed validation — expected 24-char hex ObjectId, got:",
      "color: #ff8800; font-weight: bold;",
      { documentId, type: typeof documentId, length: documentId?.length }
    );
  }

  // Phase 5 — EventLog query
  const {
    data: eventData,
    isFetching: isFetchingEvents,
    isError: isErrorEvents,
  } = useQuery({
    queryKey: ["eventLogByDocumentId", "ActiveQueue", documentId, isConvexOnly ? "cx" : "lambda"],
    queryFn: async () => {
      const body = buildEventLogByDocumentIdRequestBody({
        targetCollection: "ActiveQueue",
        documentId,
        database: mongoDatabase,
      });
      if (isConvexOnly) {
        return convexReadEventLog(
          refs.getEventLogByDocumentId,
          { targetCollection: "ActiveQueue", documentId },
          "getEventLogByDocumentId"
        );
      }
      // #163 — follow the Lambda chunk cursor so a long history is never
      // truncated by the 6 MB API Gateway cap.
      return fetchAllLambdaChunks(
        async (cursor) => {
          const response = await fetch(MONGODB_AWSGW_QUERIES, {
            method: "POST",
            mode: "cors",
            credentials: "omit",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              "x-api-key": MONGODB_AWSGW_APIKEY,
            },
            body: JSON.stringify(cursor ? { ...body, cursor } : body),
          });
          if (!response.ok) {
            throw new Error(
              `JobHistorySection getEventLogByDocumentId HTTP ${response.status}`
            );
          }
          return response.json();
        },
        ["events"]
      );
    },
    select: (raw) => parseEventLogResponse(raw, "getEventLogByDocumentId"),
    enabled: isValidDocId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
  });

  // Phase 6 — BlackoutDatesAudit query (skipped if kellerId is missing)
  const {
    data: auditData,
    isFetching: isFetchingAudit,
    isError: isErrorAudit,
  } = useQuery({
    queryKey: ["blackoutDatesAuditByKellerId", kellerId],
    queryFn: async () => {
      const body = buildBlackoutDatesAuditByKellerIdRequestBody({
        kellerId,
        database: mongoDatabase,
      });
      // #163 — blackoutDatesAudit rows are heavy (~51 KB each); follow the
      // Lambda chunk cursor so a customer's full portal-query history is never
      // truncated by the 6 MB API Gateway cap.
      return fetchAllLambdaChunks(
        async (cursor) => {
          const response = await fetch(MONGODB_AWSGW_QUERIES, {
            method: "POST",
            mode: "cors",
            credentials: "omit",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              "x-api-key": MONGODB_AWSGW_APIKEY,
            },
            body: JSON.stringify(cursor ? { ...body, cursor } : body),
          });
          if (!response.ok) {
            throw new Error(
              `JobHistorySection getBlackoutDatesAuditByKellerId HTTP ${response.status}`
            );
          }
          return response.json();
        },
        ["records"]
      );
    },
    select: (raw) => parseBlackoutDatesAuditResponse(raw),
    // #191 Stage 3 — the BlackoutDatesAudit history leg is not yet ported to
    // Convex (needs a kellerId hoist + index on the mirrored blackoutDatesAudit
    // table). convexOnly must NEVER hit the Lambda, so suppress this leg there
    // (the portal-audit rows simply don't show for convexOnly users). The
    // EventLog leg above still renders. Tracked as a Stage-2 follow-up.
    enabled: isValidKellerId && !isConvexOnly,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
  });

  // Merge both streams: EventLog entries are tagged _rowKind="event" (default),
  // BlackoutDatesAudit records are mapped into a uniform shape with
  // _rowKind="audit" and a `timestamp` field for sorting parity. Sort desc.
  const items = useMemo(() => {
    const eventItems =
      eventData && eventData.ok
        ? eventData.events.map((e) => ({ ...e, _rowKind: "event" }))
        : [];
    const auditItems =
      auditData && auditData.ok
        ? auditData.records.map((r) => ({
            // Project a synthetic event-shaped row so EventHistoryList's
            // sort + click + double-click handlers don't need a separate
            // code path. The discriminator _rowKind="audit" tells the
            // renderer to swap the visual + expansion details.
            _id: r._id,
            _rowKind: "audit",
            timestamp: r.invokedAt,
            // The full audit record is stashed under _audit for the
            // expanded JSON view. Keep the original record intact rather
            // than reshaping fields the user expects to see.
            _audit: r,
          }))
        : [];
    const all = [...eventItems, ...auditItems];
    all.sort((a, b) => {
      const ta = new Date(a.timestamp).getTime();
      const tb = new Date(b.timestamp).getTime();
      if (ta !== tb) return tb - ta;
      return String(b._id ?? "").localeCompare(String(a._id ?? ""));
    });
    return all;
  }, [eventData, auditData]);

  const isFetching = isFetchingEvents || isFetchingAudit;

  return (
    <div
      data-testid="job-history-section"
      data-tutorial-target="job-history-section"
      className="mt-4 pt-4 border-t border-skin-base relative"
      style={{ minHeight: "80px" }}
    >
      <h3 className="font-bold text-base mb-2">History</h3>
      <GridSpinner show={isFetching} label="Loading history…" />
      {!isValidDocId ? (
        <div className="text-sm italic text-skin-muted">
          {documentId == null
            ? "No record selected."
            : `Couldn't resolve a valid document ID for this job (got ${typeof documentId}). Open the browser console for details.`}
        </div>
      ) : (
        <>
          {/* Per-leg error banners — failure of one leg doesn't suppress
              the other's results. */}
          {isErrorEvents ? (
            <div className="text-sm italic mb-2" style={{ color: "#ff6b6b" }}>
              Couldn't load Scheduler change history. Please contact I.T.
            </div>
          ) : eventData && eventData.ok === false ? (
            <div className="text-sm italic mb-2" style={{ color: "#ff6b6b" }}>
              Scheduler history error: {eventData.reason || "Unknown error."}
              {eventData.lambdaVersion ? ` · Lambda ${eventData.lambdaVersion}` : ""}
            </div>
          ) : null}
          {isValidKellerId && isErrorAudit ? (
            <div className="text-sm italic mb-2" style={{ color: "#ff6b6b" }}>
              Couldn't load customer-portal audit history. Please contact I.T.
            </div>
          ) : isValidKellerId && auditData && auditData.ok === false ? (
            <div className="text-sm italic mb-2" style={{ color: "#ff6b6b" }}>
              Customer-portal audit error: {auditData.reason || "Unknown error."}
              {auditData.lambdaVersion ? ` · Lambda ${auditData.lambdaVersion}` : ""}
            </div>
          ) : null}
          <EventHistoryList
            events={items}
            showKindTag={false}
            onScrubbed={onScrubbed}
            readOnly={readOnly}
          />
        </>
      )}
    </div>
  );
};

export default JobHistorySection;
